import React, { useEffect, useState, useCallback } from 'react';
import "./ContactForm.css";

import {
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';
import SearchFilter from '../search-filter/SearchFilter';

import {
  phoneNumberRegex,
  emailRegex
} from '../../../utilities/FormUtilities';

import { State } from '../reducer/propertyReducer';
import PhoneNumberInput from '../../shared/phone-number-input/PhoneNumberInput';
import submitContactForm from '../../../utilities/SubmitContactForm';
import config from '../../../config';

interface iProps {
  comment?: string;
  onSubmitCallback?: Function;
  stateOptions: { label: string; value: string }[];
  classIOptions?: string[];
  filters?: React.RefObject<React.ElementRef<typeof SearchFilter>>;
}

export default function ContactForm(props: iProps) {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');

  const [additionalDetails, setAdditionalDetails] = useState('');

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [reCaptchaToken, setReCaptchaToken] = useState<string>();
  const [responseMessage, setResponseMessage] = useState<string>();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    try {
      const token = await executeRecaptcha('ContactUs');
      return token;

    } catch (error) {
      throw new Error("ReCaptcha Error");
    }
  }, [executeRecaptcha]);
  
  useEffect(() => {
    handleReCaptchaVerify()
      .then(token => { if (token) setReCaptchaToken(token)})
      .catch(console.error);
  }, [handleReCaptchaVerify]);

  const onSubmit = () => {
    let snapshot;
    if (props.filters?.current) {
      snapshot = props.filters?.current.getFiltersSnapshot();
    }

    const isValidPhoneNumber = phoneNumber.match(phoneNumberRegex);
    const isValidEmail = email.match(emailRegex);

    const errorMessages: string[] = [];

    if (!reCaptchaToken) {
      errorMessages.push("ReCaptcha failed.")
    }
    if (!name) {
      errorMessages.push("Name is required.")
    }
    if (!isValidPhoneNumber) {
      errorMessages.push("Valid Phone Number is required.");
    }
    if (!isValidEmail) {
      errorMessages.push("Valid Email is required.");
    }

    setValidationErrors(errorMessages);
    const validForm = errorMessages.length === 0;

    if (!validForm || !reCaptchaToken) return;

    const subject = 'GeoConnect - Properties'
    let body = `Name: ${name}\n`
      + `Phone Number: ${phoneNumber}\n`
      + `Email: ${email}\n`
      + `Company Name: ${companyName}\n\n`;

    if (snapshot) {
      body += mapFilterOptions(snapshot);
    }

    submitContactForm(reCaptchaToken, config.property.contactFormId, subject, email, body).then(response => {
      response.json().then(json => {
        setResponseMessage(json.message);

        if (json.status === 'mail_sent' && props.onSubmitCallback) {
          props.onSubmitCallback();
        }
      });
    }).catch((err) => {
      console.log(err);
      setResponseMessage("A network error occured. Please try again later.")
    });
  }

  const mapFilterOptions = (state: State): string => {
    const filterSnapShot = 
      `Building Search Options\n`
      + `Building Type: ${createList(state.bBuildingTypes)}\n`
      + `Specialty Features: ${createList(state.bSpecialtyFeatures)}\n`
      + `Zoning Description: ${createList(state.bZoning)}\n`
      + `Available Acres: ${state.bAvailableAcresMin + ' - ' + state.bAvailableAcresMax}\n`
      + `Available Square Feet: ${state.bAvailableSqFtMin + ' - ' + state.bAvailableSqFtMax}\n`
      + `Rail Served: ${state.bRailServed}\n`
      + `Building Tenancy: ${state.bMultiTenant}\n`
      + `For Sale or Lease: ${state.bSaleOrLease}\n`
      + `States: ${createList(state.bStates)}\n`
      + `Region: ${createList(state.bRegions)}\n`
      + `City: ${createList(state.bCities)}\n`
      + `County: ${createList(state.bCounties)}\n\n`
      
      + `Site Search Options\n`
      + `Specialty Features: ${createList(state.sSpecialtyFeatures)}\n`
      + `Zoning Description: ${createList(state.sZoning)}\n`
      + `Available Acres: ${state.sAvailableAcresMin + ' - ' + state.sAvailableAcresMax}\n`
      + `Rail Served: ${state.bRailServed}\n`
      + `Site Certified: ${state.sSiteCertified}\n`
      + `States: ${createList(state.sStates)}\n`
      + `Region: ${createList(state.sRegions)}\n`
      + `City: ${createList(state.sCities)}\n`
      + `County: ${createList(state.sCounties)}\n`
    ;
    return filterSnapShot;
  }

  const createList = (set: Set<string>) => {
    return Array.from(set).join(", ");
  }

  return (
    <div className="property-contact-form">
      <div className='flex-form'>
        <div className="input-container">
          <input type='text'
            value={name}
            onChange={evt => { setName(evt.target.value) }}
            name='your-name'
            placeholder='*Name' />
        </div>
        <div className="input-container">
          <PhoneNumberInput
            value={phoneNumber}
            onChange={(evt) => setPhoneNumber(evt.target.value)}
          />
        </div>
        <div className="input-container">
          <input type='text'
            value={email}
            onChange={evt => { setEmail(evt.target.value) }}
            name='e-mail'
            placeholder='*Email' />
        </div>
        <div className="input-container">
          <input type='text'
            value={companyName}
            onChange={evt => { setCompanyName(evt.target.value) }}
            name='Company-Name'
            placeholder='Company Name' />
        </div>
      </div>
      <div className="input-container">
        <textarea value={additionalDetails}
          onChange={evt => { setAdditionalDetails(evt.target.value) }}
          name='text-area'
          placeholder='Additional Details' />
      </div>
      <div className='small-font'>
        * Required Fields
      </div>
      <div className='small-font'>
        This site is protected by reCAPTCHA and the Google&nbsp;
        <a href="https://policies.google.com/privacy">Privacy Policy</a> and&nbsp;
        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
      </div>
      {validationErrors.map((msg, index) =>
        <div key={index} className='small-font color-orange'>
          {msg}
        </div>
      )}
      <div className="input-container">
        <button onClick={onSubmit}>Submit</button>
      </div>
      {responseMessage &&
        <div>{responseMessage}</div>
      }
    </div>
  )
}